import React, { useEffect, useState, useCallback } from "react";

import { Form, Formik } from "formik";
import { Plus } from "icons";

import cdesApi from "apis/cdeDefinitions";
import domainsApi from "apis/domains";
import survivorshipDomainBusinessRulesApi from "apis/survivorshipDomainBusinessRules";
import usersApi from "apis/users";
import { SubHeader, Button, Alert } from "clearsense-ui/components";
import PermissibleWrapper from "components/Common/PermissibleWrapper";
import { stringifyFilters } from "components/DataConsumption/utils";
import { useUserState } from "contexts/user";
import useSearchAndPaginate from "hooks/useSearchAndPaginate";
import { getPermissions } from "utils/roleBasedAccess";

import BusinessRulePane from "./BusinessRulePane";
import Filters from "./Filters";
import Tabs from "./Tabs";

import { SURVIVORSHIP_DOMAIN_BUSINESS_RULES_INITIAL_VALUES } from "../../../constants";
import { survivorshipDomainBusinessRules } from "../../../validations";

const SurvivorshipDomainBusinessRules = ({ features }) => {
  const [isShowPane, setIsShowPane] = useState(false);
  const [approvers, setApprovers] = useState([]);
  const [domains, setDomains] = useState([]);
  const [contexts, setContexts] = useState([]);
  const [cdes, setCdes] = useState([]);
  const [ruleTypes, setRulesTypes] = useState([]);
  const [rules, setRules] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [filters, setFilters] = useState({
    domain: "",
    critical_data_element: "",
    context: "",
    business_rule_type: "",
  });
  const [editRecord, setEditRecord] = useState({});
  const [deleteRule, setDeleteRule] = useState("");
  const [isFilterLoading, setIsFilterLoading] = useState(false);
  const [sort, setSort] = useState("business_rule_number asc");
  const [recordsCount, setRecordsCount] = useState();
  const user = useUserState();

  const userPermissions = getPermissions(features, user?.role);
  const isEdit = Object?.values(editRecord)?.length;

  const fetchRules = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const currentPage = params.get("page") || 1;
    const pageSize = params.get("per_page") || 10;
    setIsLoading(true);
    try {
      const { data } = await survivorshipDomainBusinessRulesApi.fetchRules(
        currentPage,
        pageSize,
        sort,
        stringifyFilters(filters)
      );
      setRules(data.business_rules);
      setRecordsCount(data.count);
    } catch (error) {
      logger.error(error);
    } finally {
      setIsLoading(false);
    }
  }, [sort, filters]);

  const { currentPage, pageSize, setCurrentPage, setPageSize } =
    useSearchAndPaginate(fetchRules);

  const fetchApprovers = async () => {
    try {
      const res = await usersApi.fetch(1, 300, "");
      setApprovers(res.data.users.map(({ name }) => name));
    } catch (err) {
      logger.log(err);
    }
  };

  useEffect(() => {
    fetchApprovers();
  }, []);

  const initializeFilters = async () => {
    setIsFilterLoading(true);
    try {
      const [domains, contexts, cdes, ruleTypes] = await Promise.all([
        domainsApi.fetch("", 1, 300),
        survivorshipDomainBusinessRulesApi.fetchContexts(
          stringifyFilters({ ...filters, context: null })
        ),
        cdesApi.fetchCdes("", 1, 300),
        survivorshipDomainBusinessRulesApi.fetchRuleTypes(
          stringifyFilters({ ...filters, business_rule_type: null })
        ),
      ]);
      setDomains(domains.data.domains.domain_data.map(({ name }) => name));
      setContexts(contexts.data.map(({ context }) => context));
      setCdes(cdes.data.cde_definitions.cdes.map(({ name }) => name));
      setRulesTypes(
        ruleTypes.data.map(({ business_rule_type }) => business_rule_type)
      );
    } catch (err) {
      logger.log(err);
    } finally {
      setIsFilterLoading(false);
    }
  };

  useEffect(() => {
    initializeFilters();
  }, [filters]);

  const handleSubmit = async (values, { resetForm }, initialValues) => {
    try {
      const payload = {
        business_rule_number: values.rule_no,
        business_rule: values.rule,
        context: values.context.value,
        business_rule_type: values.ruleType,
        source_name: values.sourceName.value,
        business_rule_category: values.ruleCategory,
        domain: values.domain.value,
        critical_data_element: values.cde.value,
        approver: values.approver.value,
        approval_date: values.approvalDate,
      };

      isEdit
        ? await survivorshipDomainBusinessRulesApi.updateRule(
            initialValues.rule_no,
            payload
          )
        : await survivorshipDomainBusinessRulesApi.addNewRule(payload);
      resetForm();
      setIsShowPane(false);
      fetchRules();
      setEditRecord({});
    } catch (err) {
      logger.log(err);
    }
  };

  const handleEditClick = record => {
    const {
      id,
      business_rule_number,
      business_rule,
      context,
      business_rule_type,
      source_name,
      business_rule_category,
      domain,
      critical_data_element,
      approver,
      approval_date,
    } = record;

    setEditRecord({
      id,
      rule_no: business_rule_number,
      rule: business_rule,
      context: { label: context, value: context },
      ruleType: business_rule_type,
      sourceName: { label: source_name, value: source_name },
      ruleCategory: business_rule_category,
      domain: { label: domain, value: domain },
      cde: { label: critical_data_element, value: critical_data_element },
      approver: { label: approver, value: approver },
      approvalDate: approval_date,
    });
    setIsShowPane(true);
  };

  const handleDeleteRecord = async () => {
    try {
      await survivorshipDomainBusinessRulesApi.deleteRule(deleteRule);
      setDeleteRule("");
      fetchRules();
    } catch (err) {
      logger.log(err);
    }
  };

  const handleTableChange = sorter => {
    if (!sorter.order) {
      setSort("business_rule_number asc");
    } else {
      setSort(`${sorter.field} ${sorter.order === "ascend" ? "asc" : "desc"}`);
    }
  };

  return (
    <div className="cs-ui-container px-8 space-y-8">
      <SubHeader
        breadcrumbs={[{ label: "Trust Rules Business Rules", link: "#" }]}
        className="mt-8"
        actionBlock={
          <PermissibleWrapper
            currentPermissions={userPermissions}
            acceptablePermissions="create"
          >
            <Button
              label="Add Rule"
              onClick={() => setIsShowPane(true)}
              icon={Plus}
            />
          </PermissibleWrapper>
        }
      />
      <div className="border bg-white dark:bg-cs-ui-gray-800">
        <Filters
          domains={domains}
          contexts={contexts}
          cdes={cdes}
          ruleTypes={ruleTypes}
          isFilterLoading={isFilterLoading}
          setFilters={setFilters}
        />
      </div>
      <Tabs
        isLoading={isLoading}
        rules={rules}
        userPermissions={userPermissions}
        handleEditClick={handleEditClick}
        setDeleteRule={setDeleteRule}
        handleTableChange={handleTableChange}
        paginationProps={{
          currentPage,
          pageSize,
          setCurrentPage,
          setPageSize,
          recordsCount,
        }}
      />
      <Formik
        enableReinitialize
        initialValues={
          isEdit
            ? editRecord
            : SURVIVORSHIP_DOMAIN_BUSINESS_RULES_INITIAL_VALUES
        }
        validationSchema={survivorshipDomainBusinessRules}
        onSubmit={(values, formikFunctions) =>
          handleSubmit(
            values,
            formikFunctions,
            isEdit
              ? editRecord
              : SURVIVORSHIP_DOMAIN_BUSINESS_RULES_INITIAL_VALUES
          )
        }
      >
        <Form>
          <BusinessRulePane
            approvers={approvers}
            cdes={cdes}
            domains={domains}
            isShowPane={isShowPane}
            isEdit={isEdit}
            isFilterLoading={isFilterLoading}
            setIsShowPane={setIsShowPane}
            setEditRecord={setEditRecord}
          />
        </Form>
      </Formik>
      <Alert
        size="sm"
        isOpen={!!deleteRule}
        title="Delete Business Rule"
        message="Are you sure you want to delete?"
        onClose={() => setDeleteRule("")}
        onSubmit={handleDeleteRecord}
      />
    </div>
  );
};

export default SurvivorshipDomainBusinessRules;
